/* eslint-disable react/jsx-no-useless-fragment */
import {
  AmountUnitTypeEnum,
  IAccountRespSchema,
  IOfferingRes,
  IQuoteOfferingRemoveReqSchema,
  IQuoteOfferingReqSchema,
  IQuoteOfferingRespSchema,
  IQuotePrice,
  IQuoteRespSchema,
  IRateResSchema,
  PriceDisplayEnum,
  QuoteItemAmendmentStatusEnum,
  QuoteOfferingGroupRemovalModeEnum,
  QuoteOfferingProps,
  QuoteOfferingRemovalScopeEnum,
  QuoteOfferingState,
  QuoteStatusEnum,
} from '@monetize/types/app';
import { UseFormReturn } from 'react-hook-form';
import { BsTrash } from 'react-icons/bs';
import {
  MBox,
  MButton,
  MCenter,
  MCircularProgress,
  MCustomIconButton,
  MFlex,
} from '../../../../../components/Monetize';
import { QuoteOfferingConfigurationDrawer } from '../../../../../components/Quotes/QuoteOfferingConfigurationDrawer';
import { logger } from '../../../../../services/logger';
import { canOfferingCarryPriceUplift } from '../../../../../utils/quoteRenewalUplift.utils';
import { useQuoteContext } from '../../quoteContext';
import { QuoteOfferingAmount } from './QuoteOfferingAmount';
import { prepareQuoteOfferingRequestDataFromResponse } from './quoteOfferingUtils';

export interface QuoteOfferingActionsProps extends Pick<
  QuoteOfferingProps,
  'quoteOffering' | 'isChildOffering' | 'childQuoteOfferings' | 'onChange'
> {
  account: IAccountRespSchema | Pick<IAccountRespSchema, 'id' | 'accountName'>;
  quoteOfferingState: QuoteOfferingState;
  offering?: IOfferingRes | null;
  quoteOfferingLoading: boolean;
  isOfferingOpen: boolean;
  quoteOfferingFormState: UseFormReturn<IQuoteOfferingReqSchema>;
  offeringRate: IRateResSchema | null;
  quotePrices: IQuotePrice[];
  updateQuoteOffering: (
    id: string,
    data: IQuoteOfferingReqSchema,
  ) => Promise<IQuoteOfferingRespSchema | null>;
  removeQuoteOffering: (
    data: IQuoteOfferingRemoveReqSchema,
  ) => Promise<IQuoteRespSchema | null>;
  handleQuoteOfferingUpdate: ({
    priceDisplay,
  }: {
    priceDisplay?: PriceDisplayEnum;
  }) => void;
  isOfferingLoading: boolean;
  isOfferingDeleting: boolean;
  anyOfferingLoading: boolean;
}

/**
 * Manages the quote offering menu based on the state of the quote offering
 */
export const QuoteOfferingActions = ({
  account,
  quoteOffering,
  isChildOffering,
  childQuoteOfferings = [],
  onChange: onOfferingChange,
  quoteOfferingState,
  offering,
  quoteOfferingLoading,
  isOfferingOpen,
  quoteOfferingFormState,
  offeringRate,
  quotePrices,
  updateQuoteOffering,
  removeQuoteOffering,
  handleQuoteOfferingUpdate,
  isOfferingLoading,
  isOfferingDeleting,
  anyOfferingLoading,
}: QuoteOfferingActionsProps) => {
  const { isNewQuoteOfferingForm, isRemoved } = quoteOfferingState;

  const { quoteData, quoteStateData, isLoading, isReadOnly, useAmendmentV2 } =
    useQuoteContext();
  const { quote } = quoteData;

  const { setOfferingLoadingState } = quoteStateData;

  const isDraft = quote?.status === QuoteStatusEnum.DRAFT;

  const showQuoteOfferingConfDrawer =
    quoteOffering &&
    offering &&
    canOfferingCarryPriceUplift(quoteOffering.billingFrequency, offering.type);

  const { watch, setValue, getValues } = quoteOfferingFormState;
  const watchOffering = watch('offeringId');

  /**
   * handles deletion of quote offering with child offerings
   */
  const handleDelete = async () => {
    try {
      if (quoteOffering?.id) {
        setOfferingLoadingState((prevValue) => ({
          ...prevValue,
          [quoteOffering.id]: {
            id: quoteOffering.id,
            isLoading: false,
            isDeleting: true,
          },
        }));
        if (!useAmendmentV2 && isDraft) {
          // on new and renewal quote
          await removeQuoteOffering({
            removal_scope: QuoteOfferingRemovalScopeEnum.GROUP,
            group_removal_mode: QuoteOfferingGroupRemovalModeEnum.CONTRACT,
            group_id: quoteOffering.id,
          });
        } else {
          await removeQuoteOffering({
            removal_scope: QuoteOfferingRemovalScopeEnum.TARGET,
            target_id: quoteOffering.id,
          });
        }
        /**
         * Making sure to manually update the quote data after
         * deleting quote offering successfully
         */
        if (quote) {
          const quoteOfferingsToDelete = quote.quoteOfferings.filter(
            ({ id, parentQuoteOfferingId, items }) =>
              (id === quoteOffering.id ||
                parentQuoteOfferingId === quoteOffering.id) &&
              items.find(
                ({ amendmentStatus }) =>
                  amendmentStatus === QuoteItemAmendmentStatusEnum.ADDED,
              ),
          );
          await onOfferingChange('DELETE', quoteOfferingsToDelete);
        }

        setOfferingLoadingState((prevValue) => {
          const value = { ...prevValue };
          delete value[quoteOffering.id];
          return value;
        });
      }
    } catch (error) {
      logger.error('Error while deleting quote offering ====>', error);
    }
  };

  /**
   * handles restoration of quote offering with child offerings
   */
  const handleRestore = async () => {
    try {
      if (quoteOffering?.id) {
        setOfferingLoadingState((prevValue) => ({
          ...prevValue,
          [quoteOffering.id]: {
            id: quoteOffering.id,
            isLoading: true,
            isDeleting: true,
          },
        }));

        for (const childQuoteOffering of childQuoteOfferings) {
          const quoteOfferingReq =
            prepareQuoteOfferingRequestDataFromResponse(childQuoteOffering);
          await updateQuoteOffering(childQuoteOffering.id, quoteOfferingReq);
        }

        const formValues = getValues();
        const normalizedValues: IQuoteOfferingReqSchema = {
          ...formValues,
          items: formValues.items?.map((item) => ({
            ...item,
            customDiscountType:
              item.customDiscountAmountOrPercent &&
              Number(item.customDiscountAmountOrPercent) > 0
                ? item.customDiscountType || AmountUnitTypeEnum.PERCENTAGE
                : null,
          })),
        };

        const newOfferingResponse = await updateQuoteOffering(
          quoteOffering.id,
          normalizedValues,
        );

        if (newOfferingResponse) {
          await onOfferingChange('UPDATE', newOfferingResponse);
        }

        setOfferingLoadingState((prevValue) => {
          const value = { ...prevValue };
          delete value[quoteOffering.id];
          return value;
        });
      }
    } catch (error) {
      logger.error('Error while restoring quote offering ====>', error);
    }
  };

  return (
    <>
      <MFlex alignItems="center" gridColumn="1 / -1" justifyContent="flex-end">
        {!isChildOffering && !isNewQuoteOfferingForm && (
          <>
            {!isRemoved ? (
              <>
                {!isOfferingOpen && (
                  <QuoteOfferingAmount
                    childQuoteOfferings={childQuoteOfferings}
                    quoteOffering={quoteOffering}
                    currency={quote?.currency}
                  />
                )}

                {showQuoteOfferingConfDrawer && isOfferingOpen && (
                  <QuoteOfferingConfigurationDrawer
                    account={account}
                    isDisabled={quoteOfferingLoading || anyOfferingLoading}
                    onOfferingChange={onOfferingChange}
                    childQuoteOfferings={childQuoteOfferings}
                    quoteOffering={quoteOffering}
                    isReadOnly={isReadOnly}
                    handleQuoteOfferingUpdate={handleQuoteOfferingUpdate}
                    offering={offering}
                    offeringRate={offeringRate}
                    quote={quote}
                    isOfferingLoading={isOfferingLoading}
                    quotePrices={quotePrices}
                    updateQuoteOffering={updateQuoteOffering}
                  />
                )}
                {isOfferingOpen && (
                  <MCustomIconButton
                    variant="icon"
                    btnSize={4}
                    containerSize={6}
                    p={3}
                    icon={BsTrash}
                    aria-label="Delete"
                    onClick={handleDelete}
                    isDisabled={quoteOfferingLoading || isReadOnly}
                  />
                )}
                {isOfferingDeleting && (
                  <MBox
                    position="absolute"
                    top={0}
                    left={0}
                    width="100%"
                    height="100%"
                    backgroundColor="white"
                    opacity={0.75}
                  >
                    <MCenter height="100%" justifyContent="flex-end" pr="2">
                      <MCircularProgress isIndeterminate size="2xs" />
                    </MCenter>
                  </MBox>
                )}
              </>
            ) : (
              <>
                {!isChildOffering && !quoteOfferingLoading && (
                  <MFlex
                    alignItems="center"
                    alignSelf="flex-start"
                    gridColumn="3/-1"
                    justifySelf="flex-end"
                  >
                    <MButton
                      variant="tertiary"
                      size="sm"
                      disabled={isReadOnly}
                      onClick={handleRestore}
                    >
                      Restore
                    </MButton>
                  </MFlex>
                )}
              </>
            )}
          </>
        )}
        {isNewQuoteOfferingForm && watchOffering && (
          <MCustomIconButton
            variant="icon"
            btnSize={4}
            containerSize={6}
            p={3}
            icon={BsTrash}
            aria-label="Delete"
            onClick={() => setValue('offeringId', '')}
            isDisabled={isLoading || quoteOfferingLoading || isReadOnly}
            mr="1"
          />
        )}
      </MFlex>
      {/* Expanded, the offering total stays in the header under the actions,
          spanning every column but the trailing one so it ends in the Amount
          column like the product prices */}
      {isOfferingOpen &&
        !isChildOffering &&
        !isNewQuoteOfferingForm &&
        !isRemoved && (
          <MFlex gridColumn="1 / -2" justifyContent="flex-end">
            <QuoteOfferingAmount
              childQuoteOfferings={childQuoteOfferings}
              quoteOffering={quoteOffering}
              currency={quote?.currency}
            />
          </MFlex>
        )}
    </>
  );
};
